import { create } from 'zustand';
import { ApiService, UserType } from '@/shared/services/api.service';
import { WelcomeGuestContactDto, WelcomeGuestContactDtoSchema } from 'nexgen-types';
import { clearGuestLocalStorage } from '@/utils/guestLogoutUtils';
import { persist, devtools } from 'zustand/middleware';
import { useGuestWorkplaceStore } from '../guest/stores/guest-workplace.store';
import { useWorkplaceStore } from './workplace.store';
import { useFloorManagerStore } from '@/apps/office/stores/FloorManagerStore';

// Extended type for visit details that includes the full response structure
interface VisitDetails {
  pid: string;
  check_in_time: string;
  check_out_time: string | null;
  notes: string | null;
  uuid: string;
  guest: {
    pid: string;
    name: string;
    email: string;
    phone: string | null;
    avatar_url: string | null;
    avatar_thumbnail_url?: string | null;
    company_name: string | null;
    company_job_title: string | null;
  };
  host_pid: string;
  host_email: string;
  host_moniker: string;
  organization_pid: string;
  organization_name: string;
  organization_slug: string;
  host_name: string;
}

interface GuestPresence {
  room_pid: string;

  floor_pid: string;

  /** Host the guest is placed with; kept across room/floor updates so presence self-asserts can name it. */
  host_pid?: string;
}

interface GuestState {
  step: 1 | 2 | 3; // Step 1: Name/Email → Step 2: Visit Info → Step 3: Media Devices
  guest: any | null;
  // guest: WelcomeGuestContactDtoSchema | null;
  selectedHost: { uuid: string; name: string } | null;
  visitDetails: VisitDetails | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  lobbyDetails: Lobby;
  guestPresence: GuestPresence;

  setStep: (step: 1 | 2 | 3) => void;
  setGuestInfo: (guest: WelcomeGuestContactDto) => boolean;
  setSelectedHost: (host: { uuid: string; name: string }) => void;
  setVisitDetails: (details: VisitDetails) => boolean;
  loginGuest: () => Promise<void>;
  refreshGuestToken: () => Promise<void>;
  resetGuest: () => void;
  /**
   * `skipPresence`: don't self-assert presence after the visit. An auditorium guest who
   * arrives before the session starts must not be placed anywhere (the bare call drops
   * them into the host's lobby when the host is offline).
   */
  guestLoginVisit: (options?: { skipPresence?: boolean }) => Promise<any>;
  setLobbyDetails: (imageUrl: string) => void;
  setGuestPresence: (presence: GuestPresence) => void;
}
interface Lobby {
  pid: string;
  name: string;
  slug: string;
  background_url: string;
  type: string;
  status: string;
  message: string;
  floor_pid: string;
  floor_type: string;
}

export const useGuestStore = create<GuestState>()(
  devtools(
    persist(
      (set, get) => ({
        step: 1,
        guest: null,
        selectedHost: null,
        visitDetails: null,
        token: null,
        refreshToken: null,
        loading: false,
        error: null,
        lobbyDetails: {
          pid: '',
          name: '',
          slug: '',
          background_url: '',
          type: '',
          status: '',
          message: '',
          floor_pid: '',
          floor_type: '',
        },
        guestPresence: {
          room_pid: '',
          floor_pid: '',
        },

        setStep: (step) => set({ step }),

        setGuestInfo: (guest: Partial<WelcomeGuestContactDto>) => {
          const result = WelcomeGuestContactDtoSchema.safeParse(guest);
          if (!result.success) {
            console.error('Guest info validation failed:', result.error.errors);
            set({ error: result.error.errors.map((err) => err.message).join(', ') });
            return false;
          }

          console.log('Guest info is valid:', result.data);
          set({ guest: result.data, error: null });
          return true;
        },

        setLobbyDetails: (lobby: any) => set({ lobbyDetails: lobby }),
        setSelectedHost: (host) => set({ selectedHost: host }),
        setGuestPresence: (presence) => {
          const state = get();
          // Merge rather than replace: room/floor-only updates (summon, bootstrap) used to wipe
          // host_pid, leaving the guest's presence self-assert without a host to name. Fall back
          // to the visit's host so the assert works even before the first summon.
          const hostPid =
            presence.host_pid ?? state.guestPresence?.host_pid ?? state.visitDetails?.host_pid;
          const merged = {
            ...state.guestPresence,
            ...presence,
            ...(hostPid ? { host_pid: hostPid } : {}),
          };
          set({ guestPresence: merged });
          // Save latest presence to sessionStorage for recovery after reload
          sessionStorage.setItem('guestPresenceData', JSON.stringify(merged));
        },
        setVisitDetails: (details) => {
          set({ visitDetails: details, error: null });
          return true;
        },

        guestLoginVisit: async (options?: { skipPresence?: boolean }): Promise<any> => {
          const { visitDetails } = get();
          if (!visitDetails) {
            set({ error: 'Visit details missing' });
            return { success: false, error: 'Visit details missing' };
          }
          set({ loading: true, error: null });

          try {
            const response = await ApiService.request<any>({
              method: 'POST',
              endpoint: 'welcome/guest/visit',
              data: visitDetails,
              userType: UserType.GuestUser,
            });

            set({
              visitDetails: { ...response.data.visit },
              loading: false,
              lobbyDetails: { ...response.data.lobby },
            });

            // Save complete visit details and lobby info for reload recovery
            const visitSessionData = {
              visitDetails: response.data.visit,
              lobbyDetails: response.data.lobby,
              roomId: response.data.lobby?.pid,
              hostId: visitDetails.host_pid,
            };
            sessionStorage.setItem('guestVisitData', JSON.stringify(visitSessionData));

            if (!options?.skipPresence) {
              await ApiService.patch('/workplace/guest/presence/self', {
                host_pid: visitDetails.host_pid,
              });
            }

            return { success: true, data: response.data };
          } catch (error: any) {
            console.error('Guest login failed:', error);
            console.log('Error structure:', JSON.stringify(error, null, 2));

            // Extract error message and field-specific errors
            let errorMessage = 'An error occurred while submitting your visit. Please try again.';
            const fieldErrors: Record<string, string> = {};

            // Handle different error formats from backend
            // Format 1: Array of error objects (from Zod validation - most common format)
            // Structure: { message: [{ validation: "regex", code: "invalid_string", message: "Invalid phone number", path: ["phone"] }] }
            if (error?.message && Array.isArray(error.message)) {
              console.log('Processing error.message as array:', error.message);
              error.message.forEach((errObj: any) => {
                // Check if it's an object with path and message
                if (errObj && typeof errObj === 'object') {
                  const path = Array.isArray(errObj.path) ? errObj.path[0] : errObj.path || '';
                  const msg = errObj.message || '';

                  // Check if this error is for the phone field
                  if (
                    path === 'phone' ||
                    (typeof path === 'string' && path.toLowerCase().includes('phone'))
                  ) {
                    fieldErrors.phone = msg || 'Invalid phone number format';
                  } else if (
                    !errorMessage ||
                    errorMessage ===
                      'An error occurred while submitting your visit. Please try again.'
                  ) {
                    errorMessage = msg || 'Validation error';
                  }
                } else if (typeof errObj === 'string') {
                  // Handle string format in array
                  const lowerMsg = errObj.toLowerCase();
                  if (lowerMsg.includes('phone') || lowerMsg.includes('invalid phone')) {
                    const phoneError = errObj.includes(':')
                      ? errObj.split(':').slice(1).join(':').trim()
                      : 'Invalid phone number format';
                    fieldErrors.phone = phoneError;
                  } else if (
                    !errorMessage ||
                    errorMessage ===
                      'An error occurred while submitting your visit. Please try again.'
                  ) {
                    errorMessage = errObj;
                  }
                }
              });
            } else if (error?.message && typeof error.message === 'string') {
              // Format 2: Single string message
              const lowerMsg = error.message.toLowerCase();
              if (lowerMsg.includes('phone') || lowerMsg.includes('invalid phone')) {
                fieldErrors.phone = error.message.includes(':')
                  ? error.message.split(':').slice(1).join(':').trim()
                  : 'Invalid phone number format';
                errorMessage = fieldErrors.phone;
              } else {
                errorMessage = error.message;
              }
            }

            // Format 3: errors array from ZodValidationPipe (array of objects with path and message)
            if (error?.errors && Array.isArray(error.errors)) {
              error.errors.forEach((err: any) => {
                if (typeof err === 'string') {
                  const lowerErr = err.toLowerCase();
                  if (lowerErr.includes('phone')) {
                    fieldErrors.phone = err.includes(':')
                      ? err.split(':').slice(1).join(':').trim()
                      : 'Invalid phone number format';
                  }
                } else if (err && typeof err === 'object') {
                  // Handle object format: { path: 'phone', message: 'Invalid phone number' }
                  const path = Array.isArray(err.path) ? err.path[0] : err.path || err.field || '';
                  const message = err.message || '';

                  if (
                    path === 'phone' ||
                    (typeof path === 'string' && path.toLowerCase().includes('phone')) ||
                    message.toLowerCase().includes('phone')
                  ) {
                    fieldErrors.phone = message || 'Invalid phone number format';
                  }
                }
              });
            }

            // Format 4: Direct error object with path array (from ZodError)
            if (error?.issues && Array.isArray(error.issues)) {
              error.issues.forEach((issue: any) => {
                const path = Array.isArray(issue.path) ? issue.path.join('.') : issue.path || '';
                const message = issue.message || '';

                if (
                  path === 'phone' ||
                  path.includes('phone') ||
                  message.toLowerCase().includes('phone')
                ) {
                  fieldErrors.phone = message || 'Invalid phone number format';
                }
              });
            }

            // Normalize phone error message to be more user-friendly with examples
            if (fieldErrors.phone) {
              // Provide a helpful message with examples
              const phoneError = fieldErrors.phone.toLowerCase();
              if (
                phoneError.includes('invalid phone') ||
                phoneError.includes('phone number') ||
                phoneError.includes('regex') ||
                phoneError.includes('invalid_string')
              ) {
                fieldErrors.phone =
                  'Invalid phone number. It must start with a digit from 1-9 (cannot start with 0) and may include the + prefix at the beginning. Valid examples: +1234567890, 1234567890, +521234567890';
              }
            }

            // If we have field-specific errors, use the first one as the main error
            if (Object.keys(fieldErrors).length > 0) {
              errorMessage = Object.values(fieldErrors)[0];
            }

            set({ error: errorMessage, loading: false });
            return { success: false, error: errorMessage, fieldErrors };
          }
        },

        loginGuest: async (): Promise<void> => {
          const { guest } = get();
          if (!guest) {
            set({ error: 'Guest info missing' });
            return Promise.resolve();
          }

          set({ loading: true, error: null });

          try {
            console.log('📡 Sending guest login request...', guest);

            const response = await ApiService.request<any>({
              method: 'POST',
              endpoint: 'welcome/guest/login',
              data: guest,
            });

            console.log('🔹 Guest login response:', response);
            localStorage.setItem('isUserGuest', 'true');
            sessionStorage.setItem('isGuestSessionActive', 'true');

            // Save complete guest session data for reload recovery
            const fullGuestData = {
              ...guest,
              sub: `guest-${response.data.guest.pid}`,
              pid: response.data.guest.pid,
              avatar_url: response.data.guest.avatar_url,
            };
            sessionStorage.setItem('guestSessionData', JSON.stringify(fullGuestData));

            set({
              guest: fullGuestData,
              loading: false,
            });

            set({ step: 2 });
            return Promise.resolve();
          } catch (error: any) {
            console.error('Guest login failed:', error);
            set({ error: error.message, loading: false });
            return Promise.resolve();
          }
        },

        refreshGuestToken: async () => {
          try {
            console.log('🔄 Refreshing guest token...');

            await ApiService.request<any>({
              method: 'POST',
              endpoint: 'welcome/guest/refresh',
              data: {},
            });
          } catch (error: any) {
            console.error('❌ Failed to refresh guest token:', error);

            // If refresh token is expired or invalid, logout the guest and redirect
            const errorMessage = error?.message || error?.response?.data?.message || '';
            if (
              errorMessage.includes('expired') ||
              errorMessage.includes('Invalid') ||
              errorMessage.includes('token') ||
              error?.response?.status === 401
            ) {
              console.error('🚪 Guest refresh token expired or invalid, logging out...');
              get().resetGuest();

              // Navigate to welcome page
              const orgSlug = window.location.pathname.split('/')[1];
              const welcomeRedirect =
                orgSlug && orgSlug !== 'undefined' ? `/${orgSlug}/welcome` : '/welcome';
              window.location.href = welcomeRedirect;
            }

            throw error;
          }
        },

        resetGuest: () => {
          const guestWorkplaceStore = useGuestWorkplaceStore.getState();
          const workplaceStore = useWorkplaceStore.getState();
          const floorManagerStore = useFloorManagerStore.getState();

          set({
            step: 1,
            guest: null,
            selectedHost: null,
            visitDetails: null,
            error: null,
            loading: false,
          });

          localStorage.removeItem('isUserGuest');
          sessionStorage.removeItem('isGuestSessionActive');
          sessionStorage.removeItem('guestSessionData');
          sessionStorage.removeItem('guestVisitData');
          // NGE-681: drop the auditorium guest stash too — it outlives the guest session
          // (sessionStorage is per-tab) and would make the next guest an auditorium one.
          sessionStorage.removeItem('auditoriumContext');

          if (guestWorkplaceStore?.clearPersistedState) {
            guestWorkplaceStore.clearPersistedState();
          }
          if (workplaceStore?.clearWorkplaceState) {
            workplaceStore.clearWorkplaceState();
          }
          if (floorManagerStore?.resetState) {
            floorManagerStore.resetState();
          }

          // Clear all guest-related localStorage items completely
          clearGuestLocalStorage();
        },
      }),
      {
        name: 'guest-store', // Key for localStorage
        partialize: (state) => ({
          // Only persist these fields, exclude loading, error, and other temporary states
          step: state.step,
          guest: state.guest,
          selectedHost: state.selectedHost,
          visitDetails: state.visitDetails,
          token: state.token,
          refreshToken: state.refreshToken,
          lobbyDetails: state.lobbyDetails,
        }),
      }
    )
  )
);
